import axios from "axios";
import toQueryString from "../common/utils/to-query-string";
import downloadBlob from "../common/utils/download-blob";

const invite = (newUser) => axios.post("api/v1/auth/signup/subject", newUser);

const unblockPerson = (subjectId) => 
  axios.put(`/api/v1/subjects/${subjectId}/unblock-person`);

const getSubjectUploads = (id, params) =>
  axios.get(toQueryString(`api/v1/subjects/${id}/subject-uploads?`, params));

const getSubjectUpload = (params) =>
  axios.get(toQueryString(`api/v1/s3-bucket/download-signed-url?`, params));

const getImageUrls = ({ size = 'thumb', imageIds, withCrop = true }) =>
  axios.post(`api/v1/user-uploads/get-signed-urls`, { size, imageIds, withCrop });

const getImageContent = ({ size = 'thumb', imageIds, withCrop = true }) =>
  axios.post(`api/v1/user-uploads/get-base64-content`, { size, imageIds, withCrop });

const uploadImageChoice = async (params) => {
  try {
    // Upload the image to S3
    const response = await axios.post(`api/v1/s3-bucket/upload-image-choice`, params);

    if (!response.data?.userUpload?.id) {
      throw new Error('Upload failed: Invalid response from server - missing upload ID');
    }

    const uploadId = response.data.userUpload.id;

    try {
      // Get the signed URL for the uploaded image
      const signedUrlResponse = await axios.get(toQueryString(`api/v1/s3-bucket/download-signed-url?`, {
        uploadId,
        size: 'thumb',
        sourceType: 'image-choice'
      }));

      if (!signedUrlResponse.data?.signedUrl) {
        throw new Error('Failed to generate image preview URL');
      }

      const signedUrl = signedUrlResponse.data.signedUrl;

      return {
        data: {
          id: uploadId,
          userUpload: response.data.userUpload,
          signedUrl: signedUrl,
        }
      };
    } catch (urlError) {
      console.error('Error generating signed URL for uploaded image:', urlError);
      // Even if signed URL fails, we can still return the upload info
      return {
        data: {
          id: uploadId,
          userUpload: response.data.userUpload,
          signedUrl: null,
        },
        warning: 'Image uploaded successfully but preview may not be available'
      };
    }
  } catch (error) {
    console.error('Error uploading image choice:', error);

    // Provide specific error messages based on error type
    if (error.response?.status === 413) {
      throw new Error('Image file is too large. Please choose a smaller image.');
    } else if (error.response?.status === 415) {
      throw new Error('Invalid image format. Please upload a JPG, PNG, or GIF file.');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to upload images.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error occurred while uploading image. Please try again later.');
    } else if (error.code === 'NETWORK_ERROR' || !error.response) {
      throw new Error('Network error. Please check your internet connection and try again.');
    } else {
      throw new Error(error.message || 'Failed to upload image. Please try again.');
    }
  }
};

const getSubjectsUploads = (id, params) =>
  axios.get(toQueryString(`api/v1/subjects/subject-uploads?`, params));

const getSubjectsSimple = (query) =>
  axios.get(toQueryString("api/v1/subjects/simple?", query));

const getSubjects = (query) =>
  axios.get(toQueryString("api/v1/subjects?", query));

const getSubject = (id) => axios.get(`/api/v1/subjects/${id}`);

const resendInvite = (id) => axios.post(`/api/v1/subjects/${id}/invite`);

const deleteSubject = (subjectId) =>
  axios.post(`/api/v1/subjects/${subjectId}/delete`);

const getSubjectSurveys = (subjectId) =>
  axios.get(`/api/v1/subject-surveys/subject/${subjectId}`);

const updateSubjectData = (subjectData) =>
  axios.put("/api/v1/enrollment-responses", subjectData);

const updateSurvey = (id, response) =>
  axios.put(`/api/v1/subject-surveys/${id}`, { response });

const getSubjectTasks = (id, params) =>
  axios.get(toQueryString(`/api/v1/task-managements/subject/${id}?`, params));

const getSubjectPlan = (id, params) =>
  axios.get(toQueryString(`/api/v1/task-managements/subject/${id}/plan?`, params));

// Read-only preview of FUTURE tasks (dry-run; no SubjectTasks are created).
const getSubjectPlanPreview = (id, params) =>
  axios.get(toQueryString(`/api/v1/task-managements/subject/${id}/plan-preview?`, params));

const getHealth = (subjectId, params) =>
  axios.get(
    toQueryString(`/api/v1/subject-healths/subjects/${subjectId}?`, params)
  );

const exportHealth = (subjectId, params) =>
  axios
    .get(
      toQueryString(`/api/v1/subject-healths/subjects/${subjectId}?`, params)
    )
    .then((resp) => {
      const { content, filename } = resp.data;
      downloadBlob(content, filename);
    });

const deleteSubjects = (ids) =>
  axios.delete("api/v1/subjects/bulk", {
    data: { ids },
  });

const ApiSubjectsManagement = {
  invite,
  getSubjects,
  getSubject,
  resendInvite,
  deleteSubject,
  getSubjectSurveys,
  updateSubjectData,
  updateSurvey,
  getSubjectTasks,
  getHealth,
  exportHealth,
  getSubjectsSimple,
  getSubjectUploads,
  getSubjectUpload,
  getSubjectsUploads,
  getSubjectPlan,
  getSubjectPlanPreview,
  getImageUrls,
  getImageContent,
  uploadImageChoice,
  unblockPerson,
  deleteSubjects
};

export default ApiSubjectsManagement;
